import { type FC, useCallback, useEffect, useRef } from "react";
import { Heading, Text } from "@stubhub/broadway-web";
import { ChevronRightIcon } from "@stubhub/broadway-web/icons/functional";
import type { ContinueBrowsingBanner as ContinueBrowsingBannerModel } from "src/http/types";
import { PluralContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { type AppContext, useAppContext } from "src/shared/context/AppContext";
import { useInView } from "src/shared/hooks/useInView";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { storeAnalyticsEvent } from "src/shared/utils/analyticsevent/analyticsevent-batcher";
import { useCarousel } from "src/website-apps/shared/components/responsive-carousel/CarouselIndexContext";
import { HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { SEARCH_DISCOVERY_ENTITY_TYPES } from "src/website-apps/shared/utils/searchPageEvents";
import { contentIds } from "./contentIds";
import { continueBrowsingBannerRecipe } from "./styles";

interface ContinueBrowsingBannerProps {
  banner: ContinueBrowsingBannerModel;
}

// Homepage Long CTR (exp 104039) is built from HomeCuratedContentClicks/Impressions, so the banner must emit both to be measured.
const buildBannerEventBody = (appContext: AppContext, banner: ContinueBrowsingBannerModel, entityPosition: number) => ({
  collectionId: String(HOME_CAROUSEL_IDS.continueBrowsingBanner),
  entityId: banner.eventId,
  entityTypeId: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
  entityPosition,
  index: 0,
  eventId: banner.eventId,
  anonymousId: appContext.anonymousId,
  pageVisitId: appContext.pageVisitId,
  sessionId: appContext.sessionId,
  userId: appContext.rawUserId,
  appVersion: appContext.appVersion,
  cobrandId: appContext.cobrandId,
  clientApplicationId: appContext.clientApplicationId,
});

/**
 * "Continue looking for" card rendered above the home Recently Viewed carousel for StubHub mobile
 * web (exp 104039). The backend only sends banner data to banner variants when a banner-eligible
 * event exists, so rendering is gated on the presence of `banner.url`.
 */
export const ContinueBrowsingBanner: FC<ContinueBrowsingBannerProps> = ({ banner }) => {
  const styles = continueBrowsingBannerRecipe();
  const storePageEvent = useStorePageEvent();
  const appContext = useAppContext();
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
  const hasFiredViewEvent = useRef(false);
  // Zero-based body position of this module, provided by HomeLayoutBody's CarouselProvider.
  const { index: carouselIndex } = useCarousel();

  useEffect(() => {
    if (!inView || hasFiredViewEvent.current) {
      return;
    }
    hasFiredViewEvent.current = true;
    storePageEvent(PE_TYPE.LOADED, PE_KEYS.HOME_BANNER_VIEW, String(banner.eventId ?? ""));
    storeAnalyticsEvent(
      {
        applicationName: appContext.appName,
        applicationVersion: appContext.appVersion,
        eventName: "HomeCuratedContentImpressions",
        eventBody: buildBannerEventBody(appContext, banner, carouselIndex),
      },
      appContext.env,
    );
  }, [inView, storePageEvent, appContext, banner, carouselIndex]);

  const handleClick = useCallback(() => {
    storePageEvent(
      PE_TYPE.CLICK,
      PE_KEYS.HOME_BANNER_CLICK,
      JSON.stringify({ eventId: banner.eventId, quantity: banner.quantity }),
    );
    storeAnalyticsEvent(
      {
        applicationName: appContext.appName,
        applicationVersion: appContext.appVersion,
        eventName: "HomeCuratedContentClicks",
        eventBody: buildBannerEventBody(appContext, banner, carouselIndex),
      },
      appContext.env,
    );
  }, [storePageEvent, appContext, banner, carouselIndex]);

  if (!banner.url) {
    return null;
  }

  const quantity = banner.quantity ?? 0;
  const categoryName = banner.categoryName ?? "";

  return (
    <div className={styles.wrapper} ref={ref}>
      <a className={styles.card} href={banner.url} onClick={handleClick}>
        <div className={styles.textBlock}>
          <Heading variant="title" size="small" as="h2">
            <PluralContent
              count={quantity}
              one={{ id: contentIds.titleOne, params: [categoryName] }}
              many={() => ({ id: contentIds.titleMany, params: [categoryName] })}
            />
          </Heading>
          <Text variant="body" size="small" color="text.subtle">
            <span className={styles.meta}>
              {banner.localShortDate} · {banner.quantity}{" "}
              <PluralContent
                count={quantity}
                one={{ id: contentIds.oneTicket }}
                many={() => ({ id: contentIds.manyTickets })}
              />
              <ChevronRightIcon size={16} />
            </span>
          </Text>
        </div>
        {banner.imageUrl ? <img className={styles.thumb} src={banner.imageUrl} alt="" loading="lazy" /> : null}
      </a>
    </div>
  );
};
